import { useToast } from "@design";
import { useTranslation } from "react-i18next";
import { type AgentSummary, ApiError, type ChatSessionResponse, type ChatSessionUpdate } from "@/api";
import { useSaveChat } from "./useChatSession";

/**
 * Whether the chat has started: it has a message, or one is being sent. Until then its agent, model and brand can
 * change, even once attaching a file has created it (MHS-1038). An open chat that's still loading counts as started.
 */
export function chatStarted(
  sessionUuid: string | undefined,
  session: ChatSessionResponse | undefined,
  sending: boolean,
): boolean {
  if (sending) return true;
  if (!sessionUuid) return false;
  return !session || (session.messages?.length ?? 0) > 0;
}

/**
 * Picking the chat's agent and model before it starts. A new chat keeps the picks until it's created; a chat that
 * attaching a file created saves each pick to it, and `saving` holds sending back until every save is done (each
 * refused one says why). `save` is the brand picker's too.
 */
export function useChatSetup({
  sessionUuid,
  agents,
  pickAgent,
  models,
}: Readonly<{
  sessionUuid: string | undefined;
  /** The agents the menu offers. */
  agents: AgentSummary[];
  /** A new chat's agent pick. */
  pickAgent: (agentUuid: string | null) => void;
  models: { selectedId: string | null; select: (id: string) => void };
}>) {
  const { t } = useTranslation("chat", { keyPrefix: "composer" });
  const toast = useToast();
  const mutation = useSaveChat(sessionUuid, (error) =>
    toast.error(t(isStarted(error) ? "setupStarted" : "setupFailed")),
  );
  const save = (body: ChatSessionUpdate) => {
    if (sessionUuid) mutation.mutate({ path: { session_uuid: sessionUuid }, body });
  };
  return {
    save,
    // The saves go out one after another, so the latest one is pending until all are done
    saving: mutation.isPending,
    changeAgent: (agentUuid: string | null) => {
      if (!sessionUuid) return pickAgent(agentUuid);
      // An agent answers with its own model, AnthmAI with the one picked
      const modelId = agents.find((a) => a.uuid === agentUuid)?.modelId ?? models.selectedId;
      save(modelId ? { agentUuid, modelId } : { agentUuid });
    },
    changeModel: (id: string) => {
      models.select(id);
      save({ modelId: id });
    },
  };
}

/** The chat got a message meanwhile (another tab, say): its picks are fixed now. */
const isStarted = (error: unknown) => error instanceof ApiError && error.code === "CHAT_STARTED";
